import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Assets,
  Button,
  ButtonTextOrIcon,
  Feedback,
  Form,
  formatIDNumberOnChange,
  formatPostalCode,
  OptionType,
  useNumericInputField,
  useNumericInputFieldAsync,
  useRadioButtonWithValidate,
  useSelectSingle,
  useTextInputField,
} from '@foradevelopment/fora-ui';
import {
  AddEmploymentInput,
  EmployeeIdentificationType,
  EmployeeViewModel,
  EmploymentViewModel,
  EmploymentViewModelNew,
} from '@fora-monorepo/employer-my-pages-common/types';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getExactDuplicateEmployments, getPartialDuplicateEmployments } from '../../redux/employment/employmentThunks';
import { getCountriesOptions, getIDValidator } from '../utils';
import { links } from '../../router/links';
import {
  validateCity,
  validateCoAddress,
  validateFirstName,
  validateLastName,
  validatePostalCodeInternational,
  validatePostalCodeSweden,
  validateStreetAddress,
  validateStreetAddress2,
} from '../../common/utils';
import {
  AddressForm,
  EmploymentFormStepWrapper,
  HAS_PARTIAL_DUPLICATE_MATCHES,
  IdentificationTypeForm,
  PersonalInformationForm,
} from '../../components';
import { useTypedTranslation } from '../../i18n/i18n';
import { PartialDuplicateFormTwo } from '../../components/ManageEmployment/PartialDuplicateFormTwo';

export const AddEmployeeForm = ({
  goToAddEmploymentStep,
}: {
  goToAddEmploymentStep: (employmentInputData: AddEmploymentInput) => void;
}) => {
  const { t } = useTypedTranslation();
  const dispatch = useAppDispatch();
  const countries = useAppSelector(state => state.foraGlobal.countries);

  const countriesOptions: OptionType[] = useMemo(() => getCountriesOptions(countries), [countries]);

  const [errorSubmittingMessage, setErrorSubmittingMessage] = useState('');
  const [submittingForm, setSubmittingForm] = useState(false);

  // Workaround for 154514
  // Since it's not possible to create a new employment for an employee(ID) that already exists in S4I
  // (Even though there is an employment end date, but the policy still exists so it's not in fact ended, in S4I)
  // const [formerWorkerFoundBlock, setFormerWorkerFoundBlock] = useState(false);

  // IdentificationTypeForm
  const [employeeIDType, setEmployeeIDType] = useState(EmployeeIdentificationType.PersonalIdentityNumber);

  const duplicateValidator = useCallback(
    async (value: string) => {
      const result = await dispatch(getExactDuplicateEmployments({ employeeId: value }));
      if (getExactDuplicateEmployments.fulfilled.match(result)) {
        const exactDuplicateFoundResult = result.payload;
        if (exactDuplicateFoundResult) {
          setExactDuplicateOption(exactDuplicateFoundResult);
        }

        // If no exact match was found, search with only birth date
        else if (!exactDuplicateFoundResult) {
          const result = await dispatch(
            getPartialDuplicateEmployments({ employeeId: value, includeNonVerifiedCoordinationNumbers: true })
          );
          if (getPartialDuplicateEmployments.fulfilled.match(result)) {
            const partialDuplicatesFoundResult = result.payload as EmploymentViewModelNew[];
            setPartialDuplicateOptions(partialDuplicatesFoundResult);
            if (partialDuplicatesFoundResult.length > 0) {
              return [HAS_PARTIAL_DUPLICATE_MATCHES];
            }
          }
        }
      }
      setPartialDuplicateOptions([]);
      return [];
    },
    [dispatch]
  );

  // PersonalInformationForm
  const idValidator = useMemo(() => getIDValidator(employeeIDType), [employeeIDType]);
  const employeeIdInput = useNumericInputFieldAsync('', idValidator, duplicateValidator, formatIDNumberOnChange);

  // Clear PartialDuplicateForm when id type is changed or value changed
  useEffect(() => {
    setPartialDuplicateOptions([]);
  }, [employeeIDType]);

  const firstNameInput = useTextInputField('', validateFirstName);
  const lastNameInput = useTextInputField('', validateLastName);

  // PartialDuplicateForm
  const [partialDuplicateOptions, setPartialDuplicateOptions] = useState<EmploymentViewModelNew[]>([]);
  const [exactDuplicateOption, setExactDuplicateOption] = useState<EmploymentViewModel>();

  // Selected duplicate from PartialDuplicateForm
  const partialDuplicateRadioInput = useRadioButtonWithValidate(''); // value is employeeId of duplicate
  const [selectedDuplicateEmployment, setSelectedDuplicateEmployment] = useState<{
    employee: EmployeeViewModel;
    isInactive: boolean;
    policyNumber: string;
  } | null>(null);

  // AddressForm
  const countryInput = useSelectSingle('SE');

  const coStreetAddressInput = useTextInputField('', validateCoAddress);
  const streetAddressInput = useTextInputField('', validateStreetAddress);
  const streetAddress2Input = useTextInputField('', validateStreetAddress2);
  const postalCodeInputSwe = useNumericInputField('', validatePostalCodeSweden, formatPostalCode);
  const postalCodeInputIntl = useTextInputField('', validatePostalCodeInternational);
  const cityInput = useTextInputField('', validateCity);

  const needsAddress = selectedDuplicateEmployment
    ? selectedDuplicateEmployment.employee.identificationType === EmployeeIdentificationType.CoOrdinationNumber
    : employeeIDType === EmployeeIdentificationType.CoOrdinationNumber;

  const selectedActiveDuplicateEmployment = Boolean(
    selectedDuplicateEmployment && !selectedDuplicateEmployment.isInactive
  );

  const goToNextStep = async () => {
    setSubmittingForm(true);
    const errorMessages: string[] = [];

    if (!selectedDuplicateEmployment) {
      errorMessages.push(
        ...(await employeeIdInput.validateInputField()),
        ...firstNameInput.validateInputField(),
        ...lastNameInput.validateInputField()
      );
    }

    if (needsAddress) {
      errorMessages.push(
        ...coStreetAddressInput.validateInputField(),
        ...streetAddressInput.validateInputField(),
        ...streetAddress2Input.validateInputField(),
        ...cityInput.validateInputField()
      );
      if (countryInput.props.value === 'SE') {
        errorMessages.push(...postalCodeInputSwe.validateInputField());
      } else {
        errorMessages.push(...postalCodeInputIntl.validateInputField());
      }
    }

    if (errorMessages.includes(HAS_PARTIAL_DUPLICATE_MATCHES)) {
      const selectionNeededIndex = errorMessages.indexOf(HAS_PARTIAL_DUPLICATE_MATCHES);
      errorMessages.splice(selectionNeededIndex, 1); // Remove non-stopping error
      if (!partialDuplicateRadioInput.validate()) {
        errorMessages.push(t('form.handleDuplicate.hasNotMatchedErrorText'));
      }
    }

    const formValid = errorMessages.length === 0;

    if (!formValid) {
      setSubmittingForm(false);
      return;
    }

    const employmentInputData: AddEmploymentInput = {
      firstName: selectedDuplicateEmployment
        ? selectedDuplicateEmployment.employee.firstName
        : firstNameInput.props.value,
      lastName: selectedDuplicateEmployment ? selectedDuplicateEmployment.employee.lastName : lastNameInput.props.value,
      employeeId: selectedDuplicateEmployment
        ? selectedDuplicateEmployment.employee.employeeId
        : employeeIdInput.props.value,
      identificationType: selectedDuplicateEmployment
        ? selectedDuplicateEmployment.employee.identificationType
        : employeeIDType,
      ...(needsAddress && {
        addressInfo: {
          coAddress: coStreetAddressInput.props.value,
          streetAddress: streetAddressInput.props.value,
          streetAddress2: streetAddress2Input.props.value,
          postalCode:
            countryInput.props.value === 'SE'
              ? postalCodeInputSwe.props.value.replace(/\s/g, '')
              : postalCodeInputIntl.props.value,
          city: cityInput.props.value,
          countryCode: countryInput.props.value,
        },
      }),
      employmentPeriods: [],
    };

    setErrorSubmittingMessage('');

    goToAddEmploymentStep(employmentInputData);
  };

  return (
    <Form preventDefault backgroundColor="transparent">
      <EmploymentFormStepWrapper heading={t('form.addEmployee.chooseIdType')} isFirstStep>
        <IdentificationTypeForm
          disabled={!!selectedDuplicateEmployment}
          employmentIDType={employeeIDType}
          setIDType={setEmployeeIDType}
        />
      </EmploymentFormStepWrapper>
      <EmploymentFormStepWrapper heading={t('form.addEmployee.enterPersonalInformation')} isFirstStep>
        <>
          <PersonalInformationForm
            identificationType={employeeIDType}
            employeeIdInput={employeeIdInput.props}
            firstNameInput={firstNameInput.props}
            lastNameInput={lastNameInput.props}
            disabled={!!selectedDuplicateEmployment}
          />
          {(partialDuplicateOptions.length > 0 || exactDuplicateOption) && (
            <PartialDuplicateFormTwo
              partialDuplicateOptions={partialDuplicateOptions}
              partialDuplicateRadioInput={partialDuplicateRadioInput}
              setSelectedDuplicateEmployment={setSelectedDuplicateEmployment}
              employmentsLink={links.employments}
              exactDuplicateOption={exactDuplicateOption}
              inputIdType={employeeIDType}
              employeeIdInputValue={employeeIdInput.props.value}
            />
          )}
        </>
      </EmploymentFormStepWrapper>
      {!selectedActiveDuplicateEmployment && (
        <>
          {needsAddress && (
            <EmploymentFormStepWrapper heading={t('form.addEmployee.enterAddressInformation')} isFirstStep>
              <AddressForm
                coStreetAddressInput={coStreetAddressInput.props}
                streetAddressInput={streetAddressInput.props}
                streetAddress2Input={streetAddress2Input.props}
                postalCodeInput={
                  countryInput.props.value === 'SE' ? postalCodeInputSwe.props : postalCodeInputIntl.props
                }
                cityInput={cityInput.props}
                isForeign={countryInput.props.value !== 'SE'}
                countriesOptions={countriesOptions}
                countryInput={countryInput.props}
              />
            </EmploymentFormStepWrapper>
          )}

          {errorSubmittingMessage && (
            <Feedback type="error" marginBottom={40} negativeSideMargins>
              {errorSubmittingMessage}
            </Feedback>
          )}

          <Button type="button" onClick={goToNextStep} disabled={submittingForm}>
            <ButtonTextOrIcon
              icon={<Assets.Spinner color="white" width={20} center={false} />}
              textColor="white"
              isLoading={submittingForm}
            >
              {t('common.nextStep')}
            </ButtonTextOrIcon>
          </Button>
        </>
      )}
    </Form>
  );
};
